<script setup>
import { computed } from 'vue'

import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'

const props = defineProps({
	visible: { type: Boolean, default: false },
	limits: {
		type: Object,
		default: () => ({}),
	},
})

const emit = defineEmits(['update:visible', 'go-to-files', 'upgrade'])

const internalVisible = computed({
	get: () => props.visible,
	set: (v) => emit('update:visible', v),
})

const formatNumber = (value) => {
	const rounded = Math.round((value || 0) * 100) / 100
	return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/0$/, '')
}

const maxFileSizeText = computed(() => {
	const gb = props.limits.maxFileGb || 0
	return gb >= 1 ? `${formatNumber(gb)} GB` : `${formatNumber(gb * 1000)} MB`
})

const filesNumText = computed(
	() => `${props.limits.uploadedFilesCount || 0} / ${props.limits.maxFilesNum || 0} files`,
)

const fileSizeText = computed(() => {
	const count = props.limits.oversizedCount || 0
	const verb = count === 1 ? 'file exceeds' : 'files exceed'
	return `${count} ${verb} the maximum size of ${maxFileSizeText.value}`
})

const fileDurationText = computed(() => {
	const count = props.limits.overDurationCount || 0
	const verb = count === 1 ? 'file exceeds' : 'files exceed'
	return `${count} ${verb} the maximum duration of ${props.limits.maxFileDurationMin || 0} min`
})

const onGoToFiles = () => {
	emit('go-to-files')
	internalVisible.value = false
}

const onUpgrade = () => {
	emit('upgrade')
	internalVisible.value = false
}
</script>

<template>
	<ModalBox
		v-model="internalVisible"
		title="TV Playout Limit Details"
		size="sm"
		wrapper-classes="!h-auto !max-h-none"
	>
		<section class="px-6 pb-6">
			<p class="text-sm text-surface-7 m-0 mb-5">
				Your TV Playout exceeds the limits of your current plan. You can upgrade your plan or
				remove files to resolve these issues.
			</p>

			<div class="flex flex-col gap-4">
				<div v-if="limits.filesNumExceeded">
					<p class="text-tiny text-surface-7 m-0 mb-1">File limit exceeded</p>
					<p class="text-sm m-0 text-error">{{ filesNumText }}</p>
				</div>

				<div v-if="limits.fileSizeExceeded">
					<p class="text-tiny text-surface-7 m-0 mb-1">File size limit exceeded</p>
					<p class="text-sm m-0 text-error">{{ fileSizeText }}</p>
				</div>

				<div v-if="limits.fileDurationExceeded">
					<p class="text-tiny text-surface-7 m-0 mb-1">File duration limit exceeded</p>
					<p class="text-sm m-0 text-error">{{ fileDurationText }}</p>
				</div>
			</div>
		</section>

		<template #footer>
			<div class="flex items-center justify-end gap-2 px-6 pb-6">
				<FormButton type="secondary" size="md" @click="onGoToFiles">Go to Files</FormButton>
				<FormButton type="main" size="md" @click="onUpgrade">Upgrade Plan</FormButton>
			</div>
		</template>
	</ModalBox>
</template>

<style scoped></style>
